import React from 'react';
import { Dispatch } from 'redux';
import { connect } from 'react-redux';
import { FaUser } from 'react-icons/fa';
import { Link } from 'react-router-dom';

import { GetCustomersRequest, getCustomer } from 'actions/customerActions';
import Customer from 'models/CustomerModel';
import CustomerModal from 'components/modals/CustomerModal';
import { FaExternalLinkAlt } from 'react-icons/fa';
import axios from 'helpers/axios';
import config from 'helpers/config';
import CustomerSearch from 'components/common/CustomerSearch';
import CustomerFullName from 'components/common/CustomerFullName';
import { Mail, Phone, X } from 'lucide-react';
import HoverTooltip from 'components/common/HoverTooltip';

interface Props {
    dispatch: Dispatch;
    customers: Customer[];
    newCustomerLoading: boolean;
    customerSelected: Function;
    newCustomer: Customer;
    customersLoading: boolean;
    customer?: Customer;
}

interface State {
    isNewCustomerFormOpen: boolean;
    isCreateCustomerModalOpen: boolean;
    customerSearchPhrase: string;
    customersSearchResults: Customer[];
    searchPhrase: string;
    [x: string]: any;
}

class CustomerSection extends React.Component<Props, State> {
    constructor(props: Props) {
        super(props);

        this.state = {
            isNewCustomerFormOpen: false,
            isCreateCustomerModalOpen: false,
            customerSearchPhrase: '',
            customersSearchResults: [],
            searchPhrase: '',
        };

        this.getCustomers = this.getCustomers.bind(this);
        this.handleChange = this.handleChange.bind(this);
        this.modalClosed = this.modalClosed.bind(this);
    }

    searchCustomers(searchPhrase: string) {
        this.setState({ searchPhrase });
        const customerSearchRequest: GetCustomersRequest = {
            searchPhrase,
            page: 0,
            itemsPerPage: 10,
        };
        axios
            .get(`${config.apiUrl}/customer`, { params: customerSearchRequest })
            .then((res) => {
                this.setState({ customersSearchResults: [...res.data] });
            })
            .catch((_) => {
                _;
            });
    }

    handleChange(event: React.FormEvent<HTMLInputElement>) {
        const { name, value } = event.currentTarget;
        this.setState({ [name]: value });
    }

    getCustomers() {
        const { customers } = this.props;

        if (!customers) return [];

        return customers?.map((customer) => {
            return {
                key: customer.id,
                value: customer.id,
                text: customer.fullName,
            };
        });
    }

    getCustomerOverview(customer: Customer) {
        return (
            <div className=" ">
                <div className="grid grid-cols-2 gap-x-4 ">
                    <div className="font-semibold text-gray-700">
                        {customer?.phoneNumbers?.map((number) => (
                            <span key={number.id} className="flex items-center">
                                <Phone className="h-3 w-3 mr-1" />
                                <a href={`tel:${number.phoneNumber.replace(/\s/g, '')}`}>{number.phoneNumber}</a>
                            </span>
                        ))}
                    </div>
                    <div className="font-semibold text-gray-700">
                        {/* Email: <b>{customer?.primaryEmail}</b> */}
                        <span className="flex items-center">
                            <Mail className="h-3 w-3 mr-1" />
                            {customer?.primaryEmail}
                        </span>
                    </div>
                </div>
                <div className="mt-3">
                    <Link
                        to={`/customer/${customer.id}`}
                        target="_blank"
                        className="clickable hover:text-blue-800 text-blue-500 inline-block hover:underline"
                    >
                        <span className="font-semibold">More Details</span>
                        <FaExternalLinkAlt className="inline-block ml-1" />
                    </Link>
                </div>
            </div>
        );
    }

    handleCustomerSelected(customerId: string) {
        this.props.customerSelected(customerId);
        const { dispatch } = this.props;
        dispatch<any>(getCustomer(customerId));
    }

    modalClosed(customerCreated?: boolean, _?: boolean, customerId?: string) {
        if (customerCreated && customerId) {
            this.props.customerSelected(customerId);
        }
        setTimeout(() => this.setState({ isCreateCustomerModalOpen: false }), customerCreated ? 1000 : 0);
    }

    getCustomerModal() {
        return <CustomerModal onClose={this.modalClosed}></CustomerModal>;
    }

    render() {
        const { isCreateCustomerModalOpen } = this.state;
        const { customer } = this.props;
        return (
            <>
                {isCreateCustomerModalOpen ? this.getCustomerModal() : null}
                <div className="h-full divide-y-2 divide-gray-200">
                    <div className="flex w-full pb-3">
                        <span className="flex flex-row items-center mr-4">
                            <FaUser size={15} />
                            <h2 className=" pl-3 text-lg font-semibold align-bottom">
                                {!!customer ? (
                                    <Link to={`/customer/${customer.id}`} target="_blank">
                                        <CustomerFullName customerId={customer.id} customer={customer} />
                                    </Link>
                                ) : (
                                    'Select a Customer'
                                )}
                            </h2>
                        </span>
                        <div>
                            {customer?.labels?.map((label, indx) => (
                                <span
                                    key={indx}
                                    style={{ backgroundColor: label.color }}
                                    className="mr-2 px-1 py-1 rounded-lg hover:opacity-80 text-xs"
                                >
                                    <span className="font-semibold text-white inline-flex whitespace-nowrap">
                                        {label.title}
                                    </span>
                                </span>
                            ))}
                        </div>
                        <div className="ml-auto">
                            {!!customer ? (
                                <HoverTooltip content="Disconnect Customer and Vehicle.">
                                    <button
                                        className="text-gray-500 hover:bg-gray-200 p-1 rounded"
                                        onClick={() => this.props.customerSelected(null)}
                                    >
                                        <X size={24} />
                                    </button>
                                </HoverTooltip>
                            ) : (
                                <div className="ml-auto">
                                    <div className="w-full flex float-right ">
                                        <div className="w-96 ">
                                            <CustomerSearch
                                                customerSelected={(customer) =>
                                                    this.handleCustomerSelected(customer?.id!)
                                                }
                                                displayDetailsWhenSelected={true}
                                            />
                                        </div>
                                        <span className="font-semibold pt-1.5  px-5"> OR </span>
                                        <button
                                            className="bg-blue-500 px-2 rounded-2xl font-medium text-base"
                                            onClick={() =>
                                                this.setState({
                                                    isCreateCustomerModalOpen: !isCreateCustomerModalOpen,
                                                })
                                            }
                                        >
                                            + Add new
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                    <div className="w-full pt-3 h-full">{!!customer && this.getCustomerOverview(customer)}</div>
                </div>
            </>
        );
    }
}

const mapStateToProps = (state: any) => ({
    customers: state.customer.customersList,
    newCustomerLoading: state.customer.newCustomerLoading,
    newCustomer: state.customer.newCustomer,
    customersLoading: state.customer.customersLoading,
});

export default connect(mapStateToProps)(CustomerSection);
